import { useEffect, useMemo, useRef, useState } from "react"

import cn from "classnames"

import ParticipantIcon from "@components/TeeupPeople/Content/Participants/ParticipantItem/ParticipantIcon"
import { SvgIcon } from "@components/common"
import { getParticipantStatus } from "@utils/peopleUtils"

import Info from "./Info"
import InviteButtons from "./Invite/Buttons"
import InviteHeadInfo from "./Invite/HeadInfo"
import MenuButtons from "./MenuButtons"
import { TEEUP_STATUS, TeeUpCardStage, TeeupItem } from "types/teeup"
import { Participant, USER_STATUS } from "types/participant"

import { useInboxStore } from "stores/inboxStore"
import {
  MAX_PARTICIPANTS,
  getCardWhenInfo,
  getCardWhereInfo,
} from "@utils/inbox"
import {
  getStageOptionsByStatusId,
  removedStageOption,
} from "@utils/teeupStateUtils"
import StageLabel from "./StageLabel"
import TeeUpPhoto from "./TeeUpPhoto"
import getWhenSuggestionDetails from "@utils/gameplan/getWhenSuggestionDetails"
import getWhereSuggestionDetails from "@utils/gameplan/getWhereSuggestionDetails"
import { slice } from "lodash"
import Nudge from "./Nudge"
import { updateTeeupStatus } from "@actions/teeupActions"
import {
  clearGamePlan,
  setIsCreateTeeupOpen,
  updateGamePlanWhenSuggestions,
  updateGamePlanWhereSuggestions,
} from "@actions/createTeeupActions"
import { teeupCategories, teeupUserStatusPriorities } from "@config/enums"
import dayjs from "utils/dayjs"
import { useDispatch, useSelector } from "react-redux"
import { selectUserId } from "@selectors/user"
import { selectUnreadTeeupIds } from "@selectors/notification"
import { useCreateTeeupStore } from "stores/createTeeupStore"
import {
  buildRemakeDraftFromCard,
  getRemakeGameplanSuggestionsFromOptions,
} from "@utils/remakeUtils"

const findRemovedFromTeeup = (participants, userId) =>
  participants.find(
    item =>
      item.userId === userId &&
      // @ts-ignore
      item.status === teeupUserStatusPriorities.removed
  )

const highlightText = (text, searchValue) => {
  if (!searchValue.trim()) {
    return text
  }
  const regex = new RegExp(`(${searchValue})`, "gi")
  const parts = text.split(regex)

  return parts.map((part, index) =>
    regex.test(part) ? (
      <span
        key={index}
        className="bg-amber-100 py-0.5 rounded-md mix-blend-multiply">
        {part}
      </span>
    ) : (
      part
    )
  )
}

interface Props {
  teeUp: TeeupItem
  isArchiveTab?: boolean
  onClick: (teeUp: TeeupItem, removedFromById?: number | null) => void
  onAcceptInvitation?: (teeUp: TeeupItem) => void
  onDismissInvitation?: (teeUp: TeeupItem) => void
  onPreviewClick?: (teeUp: TeeupItem) => void
  refetchTeeUps?: () => void
}

const Card = ({
  teeUp,
  isArchiveTab,
  onClick,
  onAcceptInvitation,
  onDismissInvitation,
  onPreviewClick,
  refetchTeeUps,
}: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [status, setStatus] = useState<TeeUpCardStage | null>(null)
  const [isCalendarConflict, setIsCalendarConflict] = useState(false)
  // INBOX-REBUILD 2026-07-03: Ale's Grid|List toggle already switched the LAYOUT wrapper
  // (TeeUpList/index.tsx) but the card itself was still a single list-row design in both modes.
  // Read viewMode here so the card can render Ale's .tcard chrome (cover photo + overlay chip +
  // two-col plan + footer) in grid mode.
  const viewMode = useInboxStore(state => state.viewMode)
  const isGrid = viewMode === "grid"
  const setIsArchiveSnackBarOpen = useInboxStore(
    state => state.setIsArchiveSnackBarOpen
  )
  const [removedFromById, setRemovedFromById] = useState<number | null>(null)
  const setIsLoading = useInboxStore(state => state.setIsLoading)

  const users = useInboxStore(store => store.users)
  const participants = useInboxStore(state => state.teeupParticipants[teeUp.id])
  const teeUpState = useInboxStore(state => state.teeUpsState[teeUp.id])
  const setCurrentActiveTeeUp = useInboxStore(
    state => state.setCurrentActiveTeeUp
  )
  const setIsOrganizerArchiveModalOpen = useInboxStore(
    state => state.setIsOrganizerArchiveModalOpen
  )
  const setIsArchiveModalOpen = useInboxStore(
    state => state.setIsArchiveModalOpen
  )
  const searchValue = useInboxStore(state => state.searchValue)
  const gameplanOptions = useInboxStore(
    state => state.gameplanOptions[teeUp.id]
  )
  const nudges = useInboxStore(state => state.nudges[teeUp.id])
  const userId = useSelector(selectUserId)
  const dispatch = useDispatch()
  // Wave 3 NC2: "activity since last view" -- true when this teeup has >=1 UNREAD notification
  // in the NC1 feed (message/suggestion/status/mention/nudge/...). Additive to the existing
  // teeUpState.seen dot.
  const unreadTeeupIds = useSelector(selectUnreadTeeupIds)
  const hasUnreadActivity = unreadTeeupIds.has(teeUp.id)
  // FIX-2 (inbox unread-dot, 2026-07-04): the dev backend has no per-user "seen" basis to give
  // teeUpState.seen a real value -- the list endpoint hard-codes unread_count: 0
  // (api/routers/coordination_objects.py list_my_coordination_objects) and devBackendShim's
  // reshapeListResponse always returns states: {}, so teeUpState is undefined for every card and
  // `!teeUpState?.seen` was always true (Dot A rendered on every single row, permanently). Rather
  // than fake a backend truth, derive "seen" from the SAME unread-notifications signal Dot B
  // already uses: no unread activity for this TeeUp == seen. If a real per-user teeUpState.seen is
  // ever populated (future backend work) it takes precedence via `??`.
  const isSeen = teeUpState?.seen ?? !hasUnreadActivity

  const touchStartX = useRef(0)
  const touchEndX = useRef(0)

  const isInvitation = teeUp?.userstatus === USER_STATUS.INVITED
  const isGrayOut =
    teeUp.status === TEEUP_STATUS.CANCELLED ||
    teeUp.status === TEEUP_STATUS.ENDED
  // &&  teeUp.status !== TEEUP_STATUS.ENDED

  const { whenSubtitle, whenTitle, whereTitle, whereSubtitle, whereIconName } =
    useMemo(() => {
      if (!gameplanOptions) return {} as any

      // P0-GOOGLE-LOGIN (2026-07-03): option/suggestions optional-chained -- the conflicts
      // worker used to emit gameplanOptions arrays containing a literal undefined slot
      // ([annotatedWhen, undefined] for a When-only teeup), and `option.type` here crashed the
      // whole inbox into the app-shell ErrorBoundary for calendar-connected (Google) users.
      // Worker is fixed at the source; these guards make the render path crash-proof anyway.
      const when = gameplanOptions.find(option => option?.type === "when")
      const where = gameplanOptions.find(option => option?.type === "where")

      // C3b fix (2026-07-03, runtime-diagnosed on a local dev server against a TeeUp with a
      // decided When PLUS two separately-overlapping pending Whens): this used to read
      // "decided-wins" as "if any suggestion is decided, ONLY the decided suggestion's own
      // isCalendarConflict counts -- ignore every pending suggestion, even ones that conflict
      // with each other." That silently dropped the badge for exactly this shape of TeeUp: the
      // decided value itself had no conflict, so `decidedWhenSuggestions.some(...)` returned
      // false and the pending-pending overlap (confirmed via the worker's per-suggestion
      // isCalendarConflict annotations) was never looked at. The badge is a single "there is a
      // conflict worth flagging on this TeeUp" boolean -- it must be true if ANY suggestion
      // (decided or pending) carries the flag, not just the decided one(s).
      const isCalendarConflict = when?.suggestions?.some(
        suggestion => suggestion?.isCalendarConflict
      )

      setIsCalendarConflict(!!isCalendarConflict)
      return {
        ...getCardWhenInfo(when?.suggestions),
        ...getCardWhereInfo(where?.suggestions),
      }
    }, [gameplanOptions])

  // INBOX-REBUILD 2026-07-03: Planning-state rows must show the REAL suggested times/places as
  // pills, never a vote-count/summary ("2 time suggestions" is explicitly banned by spec -- we
  // have no vote concept). Only rendered when there's no decided suggestion yet (whenTitle/
  // whereTitle fall back to "---" from getCardWhenInfo/getCardWhereInfo in that case).
  const { whenPills, wherePills } = useMemo(() => {
    if (!gameplanOptions) return { whenPills: [], wherePills: [] }
    const when = gameplanOptions.find(option => option?.type === "when")
    const where = gameplanOptions.find(option => option?.type === "where")

    const pendingWhen = (when?.suggestions || []).filter(s => s && !s.isDecided)
    const pendingWhere = (where?.suggestions || []).filter(s => s && !s.isDecided)

    return {
      whenPills: pendingWhen
        .slice(0, 3)
        .map(s => getWhenSuggestionDetails(s).title)
        .filter(Boolean),
      wherePills: pendingWhere
        .slice(0, 3)
        .map(s => (getWhereSuggestionDetails(s) as any)?.title)
        .filter(Boolean),
    }
  }, [gameplanOptions])

  const isPlanningStage = status?.label === "Planning"
  // "no decided value yet" = the "---" placeholder OR undefined (gameplanOptions not hydrated).
  const showWhenPills =
    isPlanningStage &&
    (!whenTitle || whenTitle === "---") &&
    whenPills.length > 0
  const showWherePills =
    isPlanningStage &&
    (!whereTitle || whereTitle === "---") &&
    wherePills.length > 0


  // Dynamic agenda/task label per QJ display rule (#209/#277): render "Agenda N" only when
  // agenda_count > 0, "To-Dos M" only when task_count > 0; joined with " · " when both show.
  // At zero or absent/undefined for a surface, that surface's words disappear entirely.
  const agendaCount = teeUp.agenda_count ?? 0
  const taskCount = teeUp.task_count ?? 0
  const agendaTasksLabel = [
    agendaCount > 0 ? `Agenda ${agendaCount}` : null,
    taskCount > 0 ? `To-Dos ${taskCount}` : null,
  ]
    .filter(Boolean)
    .join(" · ") as string

  const timeLabel = useMemo(() => {
    const now = dayjs()
    const date = dayjs(teeUp.lastActedAt)
    // Guard a missing/unparseable date: dayjs(invalid).diff() is NaN, and !NaN is true, which
    // previously rendered "NaNs ago". Show nothing rather than NaN.
    if (!teeUp.lastActedAt || !date.isValid()) return ""
    const diff = now.diff(date, "minute")
    if (Number.isNaN(diff)) return ""

    const format = (number, devideBy) => {
      return Math.floor(number / devideBy)
    }

    // BUG-059: "diff" is in MINUTES. The old code divided the month bucket by 10080 (minutes in a
    // WEEK) and never rolled over to years, so e.g. a ~1.4-year-old TeeUp rendered "72 month ago".
    // Use minutes-per-month (~43800) and minutes-per-year (~525600), roll over to years, and use
    // >= boundaries (the old > boundaries returned undefined for exact 60 / 1440 / 43800).
    const MIN_PER_HOUR = 60
    const MIN_PER_DAY = 1440
    const MIN_PER_MONTH = 43800
    const MIN_PER_YEAR = 525600

    if (diff < 1) {
      const seconds = now.diff(date, "second")
      return `${Math.max(seconds, 0)}s ago`
    } else if (diff < MIN_PER_HOUR) {
      return `${diff}m ago`
    } else if (diff < MIN_PER_DAY) {
      return `${format(diff, MIN_PER_HOUR)}h ago`
    } else if (diff < MIN_PER_MONTH) {
      return `${format(diff, MIN_PER_DAY)}d ago`
    } else if (diff < MIN_PER_YEAR) {
      const months = format(diff, MIN_PER_MONTH)
      return `${months} month${months > 1 ? "s" : ""} ago`
    } else {
      const years = format(diff, MIN_PER_YEAR)
      return `${years} year${years > 1 ? "s" : ""} ago`
    }
  }, [teeUp.lastActedAt])

  useEffect(() => {
    const removedParticipant = findRemovedFromTeeup(
      participants,
      userId
    ) as Participant
    if (removedParticipant?.userId) {
      setStatus(removedStageOption)
      setRemovedFromById(removedParticipant.userId)
      return
    }

    const status = getStageOptionsByStatusId()?.[teeUp?.status]
    setStatus(status || null)
  }, [participants])

  const slicedParticipants = useMemo(
    () => slice(participants, 0, MAX_PARTICIPANTS),
    [participants?.length]
  ) as Participant[]

  const handleClick = () => {
    if (isInvitation) return

    onClick(teeUp, removedFromById)
  }

  const handleSwipeStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }
  const handleSwipeEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX

    if (touchStartX.current > touchEndX.current + 50) {
      setIsMenuOpen(true)
    } else if (touchStartX.current < touchEndX.current - 50) {
      setIsMenuOpen(false)
    }
  }

  const handleArchiveClick = async (isArchiveTab?: boolean) => {
    setCurrentActiveTeeUp(teeUp)

    const organizers = Object.values(participants).filter(
      participant => participant.isOrganiser
    )
    const isOrganizer = participants.some(
      item => item.isOrganiser && item.userId === userId
    )

    if (isOrganizer && organizers.length < 2) {
      setIsOrganizerArchiveModalOpen(true)
      return
    }
    if (!isArchiveTab) {
      setIsArchiveModalOpen(true)
      return
    }

    if (isArchiveTab) {
      setIsLoading(true)
      setIsArchiveSnackBarOpen(true)
      await updateTeeupStatus(teeUp?.id, teeupCategories.teeups)
      refetchTeeUps && refetchTeeUps()
    }
  }

  // WN2 (Wave 4): EDGE-BUG-11 / BUG-LC-CHROME-014 -- Remake/Duplicate had no control anywhere on
  // web. This is the LIVE default inbox-card kebab menu (TeeUpsPage.js only swaps in the legacy
  // teeupList/TeeupItem tree for the "Coming Up" view), so this is where QA actually looked.
  // Matches the mobile reference contract exactly (no backend clone route on any surface -- see
  // src/utils/remakeUtils.js): pre-fills the standard Create-TeeUp flow client-side and submits
  // through the normal create endpoint.
  const handleRemakeClick = () => {
    setIsMenuOpen(false)
    const isCancelled = teeUp.status === TEEUP_STATUS.CANCELLED
    const { whenSuggestions, whereSuggestions } =
      getRemakeGameplanSuggestionsFromOptions(gameplanOptions, isCancelled)

    dispatch(clearGamePlan())
    if (whenSuggestions.length > 0) {
      dispatch(updateGamePlanWhenSuggestions(whenSuggestions))
    }
    if (whereSuggestions.length > 0) {
      dispatch(updateGamePlanWhereSuggestions(whereSuggestions))
    }

    useCreateTeeupStore
      .getState()
      .setRemakeDraft(buildRemakeDraftFromCard(teeUp, participants, userId))

    dispatch(setIsCreateTeeupOpen(true))
  }

  // Ale's .lmeta / .tc-plan2: time and place as TWO SEPARATE SIDE-BY-SIDE columns
  // (his .lrow .lmeta{display:flex;gap:22px}, each .tc-meta{flex:1;min-width:0}).
  // Planning-state rows with no decided value render REAL suggestion pills instead
  // (actual suggested times/places -- never vote language, never "N suggestions" summaries).
  // withDivider adds his .tc-plan2::before dashed center divider (1px dashed #C9C9CE).
  const renderMetaColumns = (withDivider: boolean) => (
    <div className="flex gap-[22px] items-start">
      <div className="flex-1 min-w-0">
        {showWhenPills ? (
          <div className="flex flex-wrap gap-1">
            {whenPills.map((pill, i) => (
              <span
                key={i}
                data-testid="when-suggestion-pill"
                className="text-[12px] font-medium text-[#2F6FED] bg-[#EAF2FC] border border-[#BBD7F2] rounded-full px-2 py-0.5">
                {pill}
              </span>
            ))}
          </div>
        ) : (
          <Info iconName="clock-2" title={whenTitle} subtitle={whenSubtitle} />
        )}
      </div>

      {withDivider && (
        <div className="self-stretch w-px border-l border-dashed border-[#C9C9CE]" />
      )}

      <div className="flex-1 min-w-0">
        {showWherePills ? (
          <div className="flex flex-wrap gap-1">
            {wherePills.map((pill, i) => (
              <span
                key={i}
                data-testid="where-suggestion-pill"
                className="text-[12px] font-medium text-[#2F6FED] bg-[#EAF2FC] border border-[#BBD7F2] rounded-full px-2 py-0.5">
                {pill}
              </span>
            ))}
          </div>
        ) : (
          <Info
            iconName={whereIconName || "gray-location"}
            title={whereTitle}
            subtitle={whereSubtitle}
          />
        )}
      </div>
    </div>
  )

  const renderAvatars = () => (
    <div className="flex items-center gap-1 inbox-card-avatars">
      {slicedParticipants?.map(participant => (
        <ParticipantIcon
          avatar={participant?.avatar}
          status={getParticipantStatus(participant.status)}
          isOrganizer={participant.isOrganiser}
          defaultAvatar={participant?.username || "A"}
          className="!size-6"
          statusClassName="!size-3"
          key={`${teeUp.id}-${participant.userId}`}
        />
      ))}

      {participants?.length > MAX_PARTICIPANTS && (
        <ParticipantIcon
          avatar={""}
          defaultAvatar={`+${participants?.length - MAX_PARTICIPANTS}`}
          isFullDefaultAvatar
          className="!size-6"
          statusClassName="!size-3"
        />
      )}
    </div>
  )

  return (
    <div
      onClick={e => e.stopPropagation()}
      onTouchStart={handleSwipeStart}
      onTouchEnd={handleSwipeEnd}
      className={cn(
        "relative rounded-lg border border-neutral-300 flex cursor-pointer animate-fade",
        {
          "bg-blue-50": isInvitation,
          "bg-white": !isInvitation && !isGrayOut,
          "opacity-50 bg-white": isGrayOut && !isInvitation,
          // WINDOW-ARCH 2026-07-03 (QJ card-sizing fix): `self-start h-fit` stops the grid from
          // stretching each card to the tallest card in its row (CSS grid's default align-items:
          // stretch was reaching the cards and leaving large empty dead space at the bottom of
          // sparse cards -- Ended/Cancelled worst). Card now only takes its content height, like
          // Ale's compact .tcard.
          "flex-col self-start h-fit": isGrid,
        }
      )}>
      {/* QJ 2026-07-04: unread dot(s) anchored to the card's TOP-RIGHT CORNER -- outermost,
          above/before the status pill, three-dots menu, and avatars, and identical across both
          list and grid/invitation layouts (single shared block on the outer `relative` wrapper,
          not duplicated per-layout). Muted/lower-saturation blues consistent with this file's
          existing palette tokens (`#7FB1E3` was already used for the activity dot; `#4F7DB3`
          already used for the agenda-tasks link) -- replaces the previous saturated bg-blue-500
          "seen" dot. */}
      {(!isSeen || hasUnreadActivity) && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
          {!isSeen && (
            <div className="w-2.5 h-2.5 flex-none rounded-full bg-[#4F7DB3]" />
          )}
          {hasUnreadActivity && (
            <div
              data-testid="card-unread-activity-dot"
              className="w-2.5 h-2.5 flex-none rounded-full bg-[#7FB1E3]"
              title="New activity"
            />
          )}
        </div>
      )}
      {/* INBOX-REBUILD 2026-07-03: Ale's .tcard grid-card hero -- cover image, .statetab status
          chip overlay (top-left), .glass gradient + .ttl white overlay title (bottom). Only in
          grid mode; list mode keeps the existing 72x72 thumbnail further down. */}
      {isGrid && !isInvitation && (
        <div className="relative m-2" onClick={handleClick}>
          <TeeUpPhoto
            src={teeUp.photo}
            isInvitation={false}
            seed={teeUp.id || teeUp.name}
            variant="cover"
          />
          {status && (
            <div className="absolute left-3.5 top-3">
              <StageLabel status={status} />
            </div>
          )}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[64px] rounded-b-lg bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-2.5 left-3.5 right-3.5 text-white text-[18px] font-semibold truncate">
            {highlightText(teeUp.name, searchValue)}
          </div>
        </div>
      )}
      <div
        className="w-full p-4 transition-all duration-300 ease-in-out"
        onClick={handleClick}>
        {isInvitation && <InviteHeadInfo participant={participants[0]} />}

        {/* INBOX-REBUILD gap-fix 2026-07-03: LIST row = Ale's .lrow anatomy VERBATIM --
            thumbnail FIRST child on the LEFT (his .thumb 56x56 radius 12 flex-none), then .mid
            (toprow = title + status chip; .lmeta = time/place as two side-by-side columns),
            avatars + Organizer on the RIGHT. Grid mode + invitations keep their own layouts. */}
        {!isGrid && !isInvitation && (
          <div className="flex items-start gap-3.5">
            <TeeUpPhoto
              src={teeUp.photo}
              isInvitation={false}
              seed={teeUp.id || teeUp.name}
              variant="lrow-thumb"
            />

            {/* .mid */}
            <div className="flex-1 min-w-0">
              {/* .toprow: title + our status chip (+ conflict/unread indicators) */}
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-neutral-900 truncate">
                  {highlightText(teeUp.name, searchValue)}
                </h3>
                <StageLabel status={status} />
                {isCalendarConflict && (
                  <div className="bg-rose-50 rounded border border-red-200 px-1.5 py-0.5 flex items-center justify-center">
                    <SvgIcon name="calendar-conflict" className="!size-4" />
                  </div>
                )}
                <div className="flex-1" />
              </div>

              {/* .lmeta: two side-by-side time/place columns */}
              <div className="mt-1.5">{renderMetaColumns(false)}</div>
            </div>

            {/* invitee avatars + Organizer on the right side */}
            <div className="flex-none self-center">{renderAvatars()}</div>
          </div>
        )}

        {(isGrid || isInvitation) && (
          <>
            {!isInvitation && (
              <div className="flex gap-2 justify-between items-center">
                <div className="flex gap-2">
                  {isCalendarConflict && (
                    <div className="bg-rose-50 rounded border border-red-200 px-1.5 py-0.5 flex items-center justify-center">
                      <SvgIcon name="calendar-conflict" className="!size-4" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* invitation list rows keep their own title row (grid title lives on the cover) */}
            {!isGrid && (
              <div className="mt-2 flex items-center justify-between">
                <h3 className="text-base font-semibold text-neutral-900">
                  {highlightText(teeUp.name, searchValue)}
                </h3>

                {isInvitation && <StageLabel status={status} />}
              </div>
            )}

            <div
              className={cn("mt-2 flex items-center justify-between", {
                "mt-4": isInvitation,
              })}>
              <div className="flex-1 min-w-0">
                {/* .tc-plan2 two columns (dashed center divider in grid mode) */}
                {renderMetaColumns(isGrid)}

                <div className="mt-2">{renderAvatars()}</div>
              </div>

              {!isGrid && (
                <TeeUpPhoto
                  src={teeUp.photo}
                  isInvitation={isInvitation}
                  seed={teeUp.id || teeUp.name}
                />
              )}
            </div>
          </>
        )}

        {isInvitation && (
          <InviteButtons
            onAcceptInvitation={onAcceptInvitation}
            onDismissInvitation={onDismissInvitation}
            onPreviewClick={onPreviewClick}
            teeUp={teeUp}
          />
        )}

        {!isInvitation && (
          <div
            className="absolute right-[-30px] top-1/2 -translate-y-1/2 xs:hidden md:block"
            onClick={e => {
              e.stopPropagation()
              setIsMenuOpen(!isMenuOpen)
            }}>
            <SvgIcon name="three-dots-2" className="!size-6 cursor-pointer" />
          </div>
        )}

        {/* Ale's .tc-foot: agenda/tasks affordance (left) + timestamp (right). No real
            agenda-item/task counts exist in the inbox-list payload (see comment above), so this
            renders the "add" affordance rather than a fabricated count -- clicking it, like the
            rest of the card, navigates into the TeeUp. */}
        {!isInvitation && (
          <div className="mt-2 pt-2 border-t border-dashed border-[#C9C9CE] w-full flex items-center justify-between">
            <button
              type="button"
              data-testid="card-agenda-tasks-link"
              onClick={e => {
                e.stopPropagation()
                onClick(teeUp, removedFromById)
              }}
              className="text-[12px] font-medium text-[#4F7DB3] hover:underline">
              {agendaTasksLabel}
            </button>
            <div className="text-neutral-500 text-xs font-medium">{timeLabel}</div>
          </div>
        )}

        <Nudge
          teeUpId={teeUp.id}
          nudge={nudges}
          users={users}
          onOpen={() => onClick(teeUp, removedFromById)}
        />
      </div>

      {!isInvitation && (
        <MenuButtons
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          isArchiveTab={!!isArchiveTab}
          onArchiveClick={handleArchiveClick}
          isRemakeAvailable={isGrayOut || !!isArchiveTab}
          onRemakeClick={handleRemakeClick}
        />
      )}
    </div>
  )
}

export default Card
